import { CSSProperties } from "react";

// Deterministic SVG sector artwork — shared NewsLayer palette (violet/cyan on dark)
import { ReactNode } from "react";

const V = "#7C3AED";
const C = "#00E5FF";

type Props = { kind: string; className?: string; style?: CSSProperties };

export const SectorArt = ({ kind, className, style }: Props) => {
  const art = ARTS[kind];
  if (!art) return null;
  return (
    <svg viewBox="0 0 160 100" aria-hidden="true" focusable="false"
      className={className} style={style} preserveAspectRatio="xMidYMid slice">
      {art}
    </svg>
  );
};

const glow = (cx: number, cy: number, r: number, color: string, o: number, id: string) => (
  <>
    <defs>
      <radialGradient id={id}>
        <stop offset="0%" stopColor={color} stopOpacity={o} />
        <stop offset="100%" stopColor={color} stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx={cx} cy={cy} r={r} fill={`url(#${id})`} />
  </>
);

const ARTS: Record<string, ReactNode> = {
  web3: (
    <g>
      {glow(118, 34, 44, C, 0.28, "g-web3")}
      <g stroke={V} strokeOpacity="0.45" strokeWidth="1">
        <line x1="40" y1="72" x2="86" y2="30" /><line x1="86" y1="30" x2="132" y2="58" />
        <line x1="40" y1="72" x2="100" y2="84" /><line x1="100" y1="84" x2="132" y2="58" />
        <line x1="86" y1="30" x2="100" y2="84" /><line x1="118" y1="14" x2="86" y2="30" />
        <line x1="118" y1="14" x2="132" y2="58" />
      </g>
      <circle cx="40" cy="72" r="4" fill={V} fillOpacity="0.8" />
      <circle cx="86" cy="30" r="5" fill={C} fillOpacity="0.9" />
      <circle cx="132" cy="58" r="4" fill={V} fillOpacity="0.7" />
      <circle cx="100" cy="84" r="3" fill={C} fillOpacity="0.55" />
      <circle cx="118" cy="14" r="3" fill={V} fillOpacity="0.6" />
      <circle cx="86" cy="30" r="9" fill="none" stroke={C} strokeOpacity="0.3" />
    </g>
  ),
  defi: (
    <g>
      {glow(126, 66, 46, V, 0.26, "g-defi")}
      <path d="M 10 78 C 50 78 60 34 100 34 C 126 34 136 50 154 44" fill="none" stroke={C} strokeOpacity="0.6" strokeWidth="1.5" />
      <path d="M 10 58 C 54 58 66 84 104 82 C 128 81 140 66 156 70" fill="none" stroke={V} strokeOpacity="0.55" strokeWidth="1.5" />
      <path d="M 16 92 C 60 90 84 56 152 60" fill="none" stroke={V} strokeOpacity="0.25" strokeWidth="1" />
      <circle cx="100" cy="34" r="3.5" fill={C} fillOpacity="0.9" />
      <circle cx="104" cy="82" r="3" fill={V} fillOpacity="0.8" />
      <circle cx="60" cy="66" r="2" fill={C} fillOpacity="0.5" />
    </g>
  ),
  blockchain: (
    <g>
      {glow(110, 50, 48, C, 0.2, "g-chain")}
      <g stroke={V} strokeOpacity="0.6" strokeWidth="1.2" fill="#0d0e14">
        <rect x="34" y="52" width="26" height="26" rx="4" />
        <rect x="76" y="38" width="28" height="28" rx="4" />
        <rect x="120" y="24" width="26" height="26" rx="4" />
      </g>
      <g stroke={C} strokeOpacity="0.55" strokeWidth="1.2">
        <line x1="60" y1="63" x2="76" y2="54" /><line x1="104" y1="50" x2="120" y2="39" />
      </g>
      <rect x="82" y="44" width="16" height="16" rx="2" fill={C} fillOpacity="0.18" stroke={C} strokeOpacity="0.4" />
      <rect x="40" y="58" width="14" height="14" rx="2" fill={V} fillOpacity="0.18" />
    </g>
  ),
  crypto: (
    <g>
      {glow(112, 44, 50, "#F59E0B", 0.14, "g-crypto")}
      <circle cx="112" cy="46" r="26" fill="none" stroke={V} strokeOpacity="0.6" strokeWidth="1.4" />
      <circle cx="112" cy="46" r="19" fill="none" stroke={C} strokeOpacity="0.35" strokeWidth="1" />
      <text x="112" y="54" textAnchor="middle" fontSize="22" fontWeight="700" fill={C} fillOpacity="0.75" fontFamily="monospace">₿</text>
      <ellipse cx="112" cy="46" rx="44" ry="14" fill="none" stroke={V} strokeOpacity="0.3" transform="rotate(-18 112 46)" />
      <circle cx="72" cy="60" r="3" fill={C} fillOpacity="0.8" />
    </g>
  ),
  "government-policy": (
    <g>
      {glow(110, 40, 46, V, 0.25, "g-policy")}
      <path d="M 76 34 L 112 16 L 148 34 Z" fill="none" stroke={V} strokeOpacity="0.65" strokeWidth="1.4" />
      <g stroke={C} strokeOpacity="0.5" strokeWidth="3">
        <line x1="86" y1="42" x2="86" y2="74" /><line x1="103" y1="42" x2="103" y2="74" />
        <line x1="120" y1="42" x2="120" y2="74" /><line x1="137" y1="42" x2="137" y2="74" />
      </g>
      <line x1="72" y1="80" x2="152" y2="80" stroke={V} strokeOpacity="0.6" strokeWidth="2" />
      <line x1="78" y1="38" x2="146" y2="38" stroke={V} strokeOpacity="0.5" strokeWidth="1.5" />
    </g>
  ),
  research: (
    <g>
      {glow(120, 60, 46, C, 0.22, "g-research")}
      <polyline points="60,80 84,58 102,68 126,36 150,44" fill="none" stroke={C} strokeOpacity="0.7" strokeWidth="1.6" />
      <g fill={V} fillOpacity="0.55">
        <rect x="66" y="70" width="6" height="16" rx="1" /><rect x="80" y="62" width="6" height="24" rx="1" />
        <rect x="94" y="74" width="6" height="12" rx="1" /><rect x="108" y="56" width="6" height="30" rx="1" />
      </g>
      <circle cx="126" cy="36" r="3.5" fill={C} />
      <circle cx="84" cy="58" r="2.5" fill={C} fillOpacity="0.7" />
    </g>
  ),
  ai: (
    <g>
      {glow(112, 48, 48, C, 0.24, "g-ai")}
      <g stroke={V} strokeOpacity="0.5" strokeWidth="1">
        <line x1="70" y1="30" x2="112" y2="48" /><line x1="70" y1="66" x2="112" y2="48" />
        <line x1="112" y1="48" x2="148" y2="26" /><line x1="112" y1="48" x2="148" y2="70" />
        <line x1="70" y1="30" x2="148" y2="26" /><line x1="70" y1="66" x2="148" y2="70" />
      </g>
      <rect x="102" y="38" width="20" height="20" rx="4" fill="#0d0e14" stroke={C} strokeOpacity="0.6" strokeWidth="1.2" />
      <rect x="107" y="43" width="10" height="10" rx="2" fill={C} fillOpacity="0.3" />
      <circle cx="70" cy="30" r="3.5" fill={V} fillOpacity="0.8" />
      <circle cx="70" cy="66" r="3.5" fill={C} fillOpacity="0.7" />
      <circle cx="148" cy="26" r="3" fill={C} fillOpacity="0.8" />
      <circle cx="148" cy="70" r="3" fill={V} fillOpacity="0.7" />
    </g>
  ),
  quantum: (
    <g>
      {glow(114, 48, 46, V, 0.26, "g-quantum")}
      <ellipse cx="114" cy="48" rx="36" ry="12" fill="none" stroke={C} strokeOpacity="0.5" strokeWidth="1.2" transform="rotate(-24 114 48)" />
      <ellipse cx="114" cy="48" rx="36" ry="12" fill="none" stroke={V} strokeOpacity="0.55" strokeWidth="1.2" transform="rotate(28 114 48)" />
      <circle cx="114" cy="48" r="7" fill={V} fillOpacity="0.55" stroke={C} strokeOpacity="0.6" strokeWidth="1" />
      <circle cx="86" cy="34" r="3" fill={C} fillOpacity="0.85" />
      <circle cx="144" cy="62" r="2.5" fill={C} fillOpacity="0.6" />
      <circle cx="140" cy="30" r="2" fill={V} fillOpacity="0.7" />
    </g>
  ),
  explainers: (
    <g>
      {glow(116, 46, 44, V, 0.24, "g-explain")}
      <rect x="78" y="20" width="60" height="64" rx="6" fill="#0d0e14" stroke={V} strokeOpacity="0.55" strokeWidth="1.2" />
      <g stroke="#9CA3AF" strokeOpacity="0.5" strokeWidth="2" strokeLinecap="round">
        <line x1="88" y1="34" x2="126" y2="34" /><line x1="88" y1="44" x2="118" y2="44" />
        <line x1="88" y1="64" x2="128" y2="64" /><line x1="88" y1="74" x2="112" y2="74" />
      </g>
      <rect x="86" y="50" width="34" height="8" rx="2" fill={C} fillOpacity="0.3" stroke={C} strokeOpacity="0.4" strokeWidth="0.8" />
      <circle cx="146" cy="18" r="8" fill="none" stroke={C} strokeOpacity="0.5" />
      <text x="146" y="22" textAnchor="middle" fontSize="11" fill={C} fillOpacity="0.8" fontFamily="monospace">?</text>
    </g>
  ),
};
